import { Reference, useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import Form from '@components/forms/Form';
import {
  PackageGrantCreateDocument,
  PackageGrantCreateMutation,
  PackageGrantCreateMutationVariables,
  PackageGrantFragmentDoc
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';

type FormData = {
  hours: number;
};

type Props = {
  clientId: string;
  onCancel: () => void;
  onSuccess: () => void;
};

const PackageGrantForm = ({ clientId, onCancel, onSuccess }: Props) => {
  const initialValues = { hours: 1 };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [createPackageGrant] = useMutation<PackageGrantCreateMutation, PackageGrantCreateMutationVariables>(
    PackageGrantCreateDocument,
    {
      update: (store, { data }) => {
        if (data?.packageGrantCreate?.packageGrant) {
          store.modify({
            fields: {
              packageGrants(cachedPackageGrantRefs: readonly Reference[]) {
                const newPackageGrantRef = store.writeFragment({
                  data: data.packageGrantCreate?.packageGrant,
                  fragment: PackageGrantFragmentDoc,
                  fragmentName: 'PackageGrant'
                });
                if (newPackageGrantRef && data.packageGrantCreate?.packageGrant) {
                  return [...cachedPackageGrantRefs, newPackageGrantRef];
                }
                return [...cachedPackageGrantRefs];
              }
            },
            id: store.identify({ __typename: 'Client', id: clientId })
          });
        }
      }
    }
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      hours: yup
        .number()
        .typeError(intl.formatMessage({ id: 'errors.packageGrant.hours.number' }))
        .moreThan(0, intl.formatMessage({ id: 'errors.packageGrant.hours.tooSmall' }))
        .required(intl.formatMessage({ id: 'errors.packageGrant.hours.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    const minutes = form.getValues().hours * 60.0;
    return createPackageGrant({
      variables: {
        clientId,
        minutes
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.packageGrantCreate,
        intl,
        onError: () => {},
        onSuccess: () => {
          form.reset();
          onSuccess();
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.packageGrant'
      });
    });
  }, [clientId, createPackageGrant, form, onSuccess, intl]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <Input
        control={form.control}
        error={form.formState.errors.hours}
        label={intl.formatMessage({ id: 'forms.packageGrant.hoursLabel' })}
        name="hours"
        required
        type="number"
      />
      <FormButtons hookForm={form} onCancel={onCancel} saveKey="dictionary.save" />
    </Form>
  );
};

export default PackageGrantForm;
